import { ErrorInfo } from 'react';

interface ErrorReport {
  timestamp: string;
  url: string;
  userAgent: string;
  error: {
    message: string;
    stack?: string;
    name: string;
  };
  componentStack?: string;
  browserInfo: {
    platform: string;
    language: string;
    cookieEnabled: boolean;
    onLine: boolean;
    screenSize: string;
  };
  memoryInfo?: {
    usedJSHeapSize?: number;
    totalJSHeapSize?: number;
    jsHeapSizeLimit?: number;
  };
}

/**
 * 브라우저 정보를 수집합니다
 */
function getBrowserInfo() {
  return {
    platform: navigator.platform || 'Unknown',
    language: navigator.language || 'Unknown',
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    screenSize: `${screen.width}x${screen.height}`,
  };
}

/**
 * 메모리 정보를 수집합니다 (가능한 경우)
 */
function getMemoryInfo() {
  try {
    // @ts-expect-error - performance.memory는 Chrome에서만 사용 가능
    const memory = (performance as any).memory;
    if (memory) {
      return {
        usedJSHeapSize: memory.usedJSHeapSize,
        totalJSHeapSize: memory.totalJSHeapSize,
        jsHeapSizeLimit: memory.jsHeapSizeLimit,
      };
    }
  } catch (error) {
    // 메모리 정보를 가져올 수 없는 경우 무시
  }
  return undefined;
}

/**
 * 민감한 정보를 필터링합니다
 */
function sanitizeStack(stack?: string): string | undefined {
  if (!stack) return undefined;
  
  // 민감한 정보 패턴들을 제거
  return stack
    .replace(/[?&](token|key|password|secret)=[^&\s]*/gi, '$1=***')
    .replace(/Bearer [^\s]*/gi, 'Bearer ***')
    .replace(/apikey=[^\s&]*/gi, 'apikey=***');
}

/**
 * 에러 정보를 종합해서 수집합니다
 */
export function collectErrorInfo(
  error: Error,
  errorInfo?: ErrorInfo
): ErrorReport {
  const now = new Date();
  const timestamp = now.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'Asia/Seoul'
  });

  return {
    timestamp,
    url: window.location.href,
    userAgent: navigator.userAgent,
    error: {
      message: error.message || '알 수 없는 에러',
      stack: sanitizeStack(error.stack),
      name: error.name || 'Error',
    },
    componentStack: errorInfo?.componentStack,
    browserInfo: getBrowserInfo(),
    memoryInfo: getMemoryInfo(),
  };
}

/**
 * 에러 리포트를 사용자가 읽기 쉬운 텍스트로 변환합니다
 */
export function formatErrorReport(errorReport: ErrorReport): string {
  const lines = [
    '=== 🚨 에러 리포트 ===',
    '',
    `📅 발생 시각: ${errorReport.timestamp}`,
    `🌐 페이지: ${errorReport.url}`,
    '',
    '🐛 [에러 정보]',
    `메시지: ${errorReport.error.message}`,
    `타입: ${errorReport.error.name}`,
    '',
  ];

  // 스택 트레이스 추가
  if (errorReport.error.stack) {
    lines.push('📋 [스택 트레이스]');
    lines.push(errorReport.error.stack);
    lines.push('');
  }

  // 컴포넌트 스택 추가
  if (errorReport.componentStack) {
    lines.push('⚛️ [컴포넌트 스택]');
    lines.push(errorReport.componentStack);
    lines.push('');
  }

  // 브라우저 정보 추가
  lines.push('🌍 [브라우저 정보]');
  lines.push(`UserAgent: ${errorReport.userAgent}`);
  lines.push(`플랫폼: ${errorReport.browserInfo.platform}`);
  lines.push(`언어: ${errorReport.browserInfo.language}`);
  lines.push(`네트워크: ${errorReport.browserInfo.onLine ? '온라인' : '오프라인'}`);
  lines.push(`화면 크기: ${errorReport.browserInfo.screenSize}`);
  lines.push(`쿠키: ${errorReport.browserInfo.cookieEnabled ? '사용' : '비사용'}`);
  lines.push('');

  // 메모리 정보 추가 (있는 경우)
  if (errorReport.memoryInfo) {
    lines.push('💾 [메모리 정보]');
    lines.push(`사용 중: ${Math.round((errorReport.memoryInfo.usedJSHeapSize || 0) / 1024 / 1024)}MB`);
    lines.push(`총 크기: ${Math.round((errorReport.memoryInfo.totalJSHeapSize || 0) / 1024 / 1024)}MB`);
    lines.push(`제한: ${Math.round((errorReport.memoryInfo.jsHeapSizeLimit || 0) / 1024 / 1024)}MB`);
    lines.push('');
  }

  lines.push('📞 [고객센터]');
  lines.push('이메일: support@wholsee.kr');
  lines.push('전화: 010-3741-2556');
  lines.push('');
  lines.push('위 정보를 복사해서 고객센터로 전달해주시면');
  lines.push('빠른 문제 해결이 가능합니다.');

  return lines.join('\n');
}

/**
 * 클립보드에 텍스트를 복사합니다
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      // 최신 브라우저의 Clipboard API 사용
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // 폴백: 구형 브라우저용 방법
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      return success;
    }
  } catch (error) {
    console.error('클립보드 복사 실패:', error);
    return false;
  }
}